import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../utils/api";
import { useAuth } from "./AuthContext";

const BrandLogoContext = createContext({ logos: {}, ensureLogos: async () => {}, refreshLogos: async () => {} });

export function BrandLogoProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const loadedForUser = useRef(null);
  const loadingRequest = useRef(null);
  const currentUserId = useRef(user?.id || null);
  currentUserId.current = user?.id || null;

  const loadLogos = useCallback((force = false) => {
    const userId = user?.id;
    if (!userId) return Promise.resolve();
    if (!force && loadedForUser.current === userId) return Promise.resolve();
    if (loadingRequest.current) {
      const pending = loadingRequest.current;
      return force ? pending.then(() => loadLogos(true)) : pending;
    }

    const request = api.get("/settings/brand-logos")
      .then(({ data }) => {
        if (currentUserId.current !== userId) return;
        setItems(Array.isArray(data) ? data : []);
        loadedForUser.current = userId;
      })
      .catch((error) => {
        if (error.response?.status !== 401) console.error("Logo ayarları alınamadı", error);
      })
      .finally(() => {
        if (loadingRequest.current === request) loadingRequest.current = null;
      });
    loadingRequest.current = request;
    return request;
  }, [user?.id]);

  const ensureLogos = useCallback(() => loadLogos(false), [loadLogos]);
  const refreshLogos = useCallback(() => loadLogos(true), [loadLogos]);

  useEffect(() => {
    loadedForUser.current = null;
    loadingRequest.current = null;
    setItems([]);
  }, [user?.id]);

  const logos = useMemo(() => Object.fromEntries(items.map((item) => [item.brand_key, item])), [items]);
  const value = useMemo(() => ({ logos, items, ensureLogos, refreshLogos }), [logos, items, ensureLogos, refreshLogos]);

  return <BrandLogoContext.Provider value={value}>{children}</BrandLogoContext.Provider>;
}

export function useBrandLogos() {
  return useContext(BrandLogoContext);
}
